/* eslint-disable camelcase */
// @mui
import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, Collapse, Grid, Stack, Typography } from '@mui/material';
import { getYear } from 'date-fns';
import { useNavigate } from 'react-router';
import Image from '../../components/Image';
import { useSelector } from '../../redux/store';
import useLocales from '../../hooks/useLocales';
import useResponsive from '../../hooks/useResponsive';

// Assets
import FooterGC from '../../assets/powered/gc.webp';
import FooterBankDeposit from '../../assets/powered/bank_deposit.webp';
import FooterBmm from '../../assets/powered/bmm.webp';
import FooterGD from '../../assets/powered/godaddy.webp';
import FooterIovation from '../../assets/powered/iovation.webp';
import FooterItech from '../../assets/powered/itech.webp';
import FooterTst from '../../assets/powered/tst.webp';
import FooterAlfapay from '../../assets/powered/alfapay.webp';
import FooterFpay from '../../assets/powered/fpay.webp';
import FooterSfpay from '../../assets/powered/sfpay.webp';
import FooterSpeedpay from '../../assets/powered/speedpay.webp';
import FooterDmca from '../../assets/powered/dmca-logo.png';
import FooterTrustpilot from '../../assets/powered/trustpilot.png';
import FooterFacebook from '../../assets/home/facebook.webp';
import FooterInstagram from '../../assets/home/instagram.webp';
import FooterYoutube from '../../assets/home/youtube.webp';
import FooterWhatsapp from '../../assets/home/whatsapp.webp';
import FooterTelegram from '../../assets/home/telegram.webp';
import FooterThreatmetrix from '../../assets/powered/threatmetrix.webp';
import Footer18Plus from '../../assets/powered/18+.webp';
import FooterGamcare from '../../assets/powered/gamcare.webp';
import FooterBeGambleAware from '../../assets/powered/begambleaware.webp';
import conObj from '../../utils/connection-assistant';

const FooterTextHeader = styled(Typography)(() => ({
  color: '#3c3c3c',
  fontWeight: 500,
  fontSize: '.75rem',
}));

const FooterText = styled(Typography)(() => ({
  color: '#8c8c8c',
  fontWeight: 400,
  fontSize: '.75rem',
}));

const SeoContent = styled(Box)(({ theme }) => ({
  color: '#000',
  '& h1': {
    fontSize: '20pt',
    fontWeight: 700,
    lineHeight: 1.3,
    margin: 0,
    marginBottom: theme.spacing(2),
  },
  '& h2': {
    fontSize: '16pt',
    fontWeight: 700,
    lineHeight: 1.35,
    margin: 0,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
  },
  '& h3': {
    fontSize: '14pt',
    fontWeight: 600,
    lineHeight: 1.4,
    color: '#434343',
    margin: 0,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  '& p, & li': {
    fontSize: '11pt',
    lineHeight: 1.7,
    color: '#000',
  },
  '& p': {
    margin: 0,
    marginBottom: theme.spacing(1.5),
  },
  '& ol': {
    margin: 0,
    paddingLeft: theme.spacing(3),
  },
  '& li': {
    marginBottom: theme.spacing(1),
  },
  '& a': {
    color: '#1155cc',
    textDecoration: 'none',
  },
  '& strong': {
    fontWeight: 700,
  },
}));

const HoverImage = styled(Image)(() => ({
  cursor: 'pointer',
  filter: 'grayscale(100%)',
  transition: 'filter 0.3s ease',
  '&:hover': {
    filter: 'grayscale(0%)',
  },
}));

const Footer = () => {
  const navigate = useNavigate();

  const { siteSettings } = useSelector((x) => x.lookup);

  const { translate } = useLocales();

  const isDesktop = useResponsive('up', 'lg');

  const [showSeoContent, setShowSeoContent] = useState(false);

  const [pages, setPages] = useState([]);

  useEffect(() => {
    getPages();
  }, []);

  const { config_telegram, config_whatsapp, config_facebook, config_instagram, config_youtube } = siteSettings || {};

  const copyrightText = useMemo(() => {
    if (siteSettings?.config_footer_copyright) {
      const newText = siteSettings?.config_footer_copyright
        .replace('[year]', getYear(new Date()))
        .replace('[name]', siteSettings?.config_name);

      console.log(newText);

      return newText;
    }

    return '';
  }, [siteSettings?.config_footer_copyright, siteSettings?.config_name]);

  const openSocial = (link) => {
    window.open(
      link,
      '_blank'
      // 'noopener noreferrer'
    );
  };

  const getPages = () => {
    conObj
      .post(`/pages.json`)
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.data?.data?.length > 0) {
            setPages(res.data?.data?.data?.map((item) => ({ pageKey: item?.page_key, title: item?.title })));
          }
        }
      })
      .catch((err) => {
        console.log(`Error: ${err?.message}`);
      });
  };

  return (
    <Box
      sx={{
        color: '#ccc',
        px: {
          xs: 1,
          md: 12,
        },
        py: {
          xs: 1,
          md: 6,
        },
      }}
    >
      <Grid container spacing={4}>
        {/* About */}
        <Grid item xs={12} md={6} lg={3}>
          <FooterTextHeader>{translate('about_bd888')}</FooterTextHeader>
          <FooterText sx={{ mt: 1 }}>{translate('bd888_license_desc')}</FooterText>

          <FooterTextHeader sx={{ mt: 2 }}>{translate('gaming_license')}</FooterTextHeader>
          <Image src={FooterGC} sx={{ mt: 1, height: 30, width: 95 }} />
        </Grid>

        {!isDesktop && (
          <Grid item xs={12} md={6}>
            <FooterTextHeader>{translate('payment_method')}</FooterTextHeader>
            <Stack direction="row" spacing={1.5} mt={1} flexWrap="wrap" useFlexGap>
              <HoverImage disabledEffect src={FooterBankDeposit} sx={{ height: 20, my: 1 }} />
              <HoverImage disabledEffect src={FooterFpay} sx={{ height: 20, my: 1 }} />
              <HoverImage disabledEffect src={FooterSfpay} sx={{ height: 20, my: 1 }} />
              <HoverImage disabledEffect src={FooterSpeedpay} sx={{ height: 20, my: 1 }} />
              <HoverImage disabledEffect src={FooterAlfapay} sx={{ height: 20, my: 1 }} />
            </Stack>
          </Grid>
        )}

        {/* Information and Help Center */}
        {isDesktop && (
          <Grid item xs={12} md={6} lg={6}>
            <Grid container>
              <Grid item xs={6} md={4}>
                <FooterTextHeader>{translate('information')}</FooterTextHeader>
                <Stack spacing={0.5} mt={1} alignItems="flex-start">
                  {pages.map((item, idx) => (
                    <Box
                      aria-label={item?.title}
                      key={idx}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/page/${item?.pageKey}`)}
                    >
                      <FooterText>{translate(item?.title)}</FooterText>
                    </Box>
                  ))}
                  {/* <Box aria-label="About Us">
                    <FooterText>{translate('about_us_title')}</FooterText>
                  </Box>
                  <Box aria-label="Terms & Conditions">
                    <FooterText>{translate('terms_and_conditions')}</FooterText>
                  </Box>
                  <Box aria-label="Privacy Policy">
                    <FooterText>{translate('privacy_policy_title')}</FooterText>
                  </Box>
                  <Box aria-label="Responsible Gaming">
                    <FooterText>{translate('responsible_gaming_title')}</FooterText>
                  </Box>
                  <Box aria-label="Disconnect Policy">
                    <FooterText>{translate('disconnect_policy')}</FooterText>
                  </Box> */}
                </Stack>
              </Grid>

              <Grid item xs={6} md={4}>
                <FooterTextHeader>{translate('help_center')}</FooterTextHeader>
                <Stack spacing={0.5} mt={1} alignItems="flex-start">
                  <Box aria-label="FAQ">
                    <FooterText>{translate('faq')}</FooterText>
                  </Box>
                  <Box aria-label="Contact Us">
                    <FooterText>{translate('contact_us')}</FooterText>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Certification */}
        {isDesktop && (
          <Grid item xs={12} md={6} lg={3}>
            <FooterTextHeader>{translate('certification')}</FooterTextHeader>
            <Stack direction="row" spacing={1.5} mt={1} flexWrap="wrap" useFlexGap>
              <HoverImage disabledEffect src={FooterBmm} sx={{ height: 20, my: 1 }} />
              <HoverImage disabledEffect src={FooterGD} sx={{ height: 30, my: 1 }} />
              <HoverImage disabledEffect src={FooterItech} sx={{ height: 20, my: 1 }} />
              <HoverImage disabledEffect src={FooterTst} sx={{ height: 15, my: 1 }} />
              <Box aria-label="DMCA Protected">
                <Image disabledEffect src={FooterDmca} sx={{ height: 50, my: 1 }} />
              </Box>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  border: '2px solid #bae1cf',
                  borderRadius: '2px',
                  px: 1,
                  py: 1,
                  my: 1,
                }}
              >
                <Typography sx={{ mr: 1, fontSize: '11px', fontWeight: 700, color: '#000' }}>
                  {translate('review_us_on')}
                </Typography>
                <Image disabledEffect src={FooterTrustpilot} sx={{ height: 15 }} />
              </Box>
            </Stack>
          </Grid>
        )}
      </Grid>

      {/* Divider */}
      <Box sx={{ borderTop: '1px solid #444', my: 4 }} />

      {/* Bottom Row */}
      <Grid container spacing={4} alignItems="flex-start">
        {/* Payments */}
        {isDesktop && (
          <Grid item xs={12} md={3} lg={3}>
            <FooterTextHeader>{translate('payment_method')}</FooterTextHeader>
            <Stack direction="row" spacing={1.5} mt={1} flexWrap="wrap" useFlexGap>
              <HoverImage disabledEffect src={FooterBankDeposit} sx={{ height: { xs: 20, md: 25 }, my: 1 }} />
              <HoverImage disabledEffect src={FooterFpay} sx={{ height: { xs: 20, md: 30 }, my: 1 }} />
              <HoverImage disabledEffect src={FooterSfpay} sx={{ height: { xs: 20, md: 30 }, my: 1 }} />
              <HoverImage disabledEffect src={FooterSpeedpay} sx={{ height: { xs: 20, md: 25 }, my: 1 }} />
              <HoverImage disabledEffect src={FooterAlfapay} sx={{ height: { xs: 20, md: 25 }, my: 1 }} />
            </Stack>
          </Grid>
        )}

        {/* Mobile Certification */}
        {!isDesktop && (
          <Grid item xs={6} md={6}>
            <FooterTextHeader>{translate('certification')}</FooterTextHeader>
            <Stack direction="row" spacing={1.5} mt={1} flexWrap="wrap" useFlexGap>
              <HoverImage disabledEffect src={FooterBmm} sx={{ height: 15, my: 1 }} />
              <HoverImage disabledEffect src={FooterGD} sx={{ height: 20, my: 1 }} />
              <HoverImage disabledEffect src={FooterItech} sx={{ height: 20, my: 1 }} />
              <HoverImage disabledEffect src={FooterTst} sx={{ height: 15, my: 1 }} />
              <Box
                aria-label="Trustpilot"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  border: '2px solid #bae1cf',
                  borderRadius: '2px',
                  p: 1,
                  my: 1,
                }}
              >
                <Typography sx={{ mr: 1, fontSize: '11px', fontWeight: 700, color: '#000' }}>
                  {translate('review_us_on')}
                </Typography>
                <Image src={FooterTrustpilot} sx={{ height: 'auto' }} />
              </Box>
            </Stack>
          </Grid>
        )}

        {/* Social */}
        <Grid item xs={6} md={3} lg={3}>
          <FooterTextHeader>{translate('follow_us')}</FooterTextHeader>
          <Stack direction="row" spacing={1.5} mt={1} flexWrap="wrap" useFlexGap>
            {config_facebook ? (
              <Box aria-label="Facebook" onClick={() => openSocial(config_facebook)}>
                <HoverImage disabledEffect src={FooterFacebook} sx={{ height: { xs: 20, md: 30 }, my: 1 }} />
              </Box>
            ) : (
              ''
            )}
            {config_instagram ? (
              <Box aria-label="Instagram" onClick={() => openSocial(config_instagram)}>
                <HoverImage disabledEffect src={FooterInstagram} sx={{ height: { xs: 20, md: 30 }, my: 1 }} />
              </Box>
            ) : (
              ''
            )}
            {config_youtube ? (
              <Box aria-label="YouTube" onClick={() => openSocial(config_youtube)}>
                <HoverImage disabledEffect src={FooterYoutube} sx={{ height: { xs: 20, md: 30 }, my: 1 }} />
              </Box>
            ) : (
              ''
            )}
            {config_whatsapp ? (
              <Box aria-label="WhatsApp" onClick={() => openSocial(config_whatsapp)}>
                <HoverImage disabledEffect src={FooterWhatsapp} sx={{ height: { xs: 20, md: 30 }, my: 1 }} />
              </Box>
            ) : (
              ''
            )}
            {config_telegram ? (
              <Box aria-label="Telegram" onClick={() => openSocial(config_telegram)}>
                <HoverImage disabledEffect src={FooterTelegram} sx={{ height: { xs: 20, md: 30 }, my: 1 }} />
              </Box>
            ) : (
              ''
            )}
          </Stack>
        </Grid>

        {/* Security */}
        <Grid item xs={6} md={3} lg={3}>
          <FooterTextHeader>{translate('security')}</FooterTextHeader>
          <Stack direction="row" spacing={1.5} mt={1} flexWrap="wrap" useFlexGap>
            <HoverImage disabledEffect src={FooterIovation} sx={{ height: { xs: 15, md: 20 }, my: 1 }} />
            <HoverImage disabledEffect src={FooterThreatmetrix} sx={{ height: { xs: 20, md: 25 }, my: 1 }} />
          </Stack>
        </Grid>

        {/* Responsible Gaming */}
        <Grid item xs={6} md={3} lg={3}>
          <FooterTextHeader>{translate('responsible_gaming_title')}</FooterTextHeader>
          <Stack direction="row" spacing={1.5} mt={1} flexWrap="wrap" useFlexGap>
            <HoverImage disabledEffect src={Footer18Plus} sx={{ height: { xs: 20, md: 25 }, my: 1 }} />
            <HoverImage disabledEffect src={FooterGamcare} sx={{ height: { xs: 20, md: 25 }, my: 1 }} />
            <HoverImage disabledEffect src={FooterBeGambleAware} sx={{ height: { xs: 20, md: 25 }, my: 1 }} />
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderTop: '1px solid #444', my: 4 }} />

        <Box>
          <Collapse in={showSeoContent} collapsedSize={240}>
            <SeoContent>
              <Typography component="h1">{translate('footer_seo_title')}</Typography>

              <Typography component="p">{translate('footer_seo_intro')}</Typography>

              <Typography component="h2">{translate('footer_seo_why_title')}</Typography>

              <Typography component="h3">{translate('footer_seo_secure_title')}</Typography>
              <Typography component="p">{translate('footer_seo_secure_description')}</Typography>

              <Typography component="h3">{translate('footer_seo_bonus_title')}</Typography>
              <Typography component="p">{translate('footer_seo_bonus_description')}</Typography>

              <Typography component="h3">{translate('footer_seo_payment_title')}</Typography>
              <Typography component="p">{translate('footer_seo_payment_description')}</Typography>

              <Typography component="h3">{translate('footer_seo_support_title')}</Typography>
              <Typography component="p">{translate('footer_seo_support_description')}</Typography>

              <Typography component="h2">{translate('footer_seo_games_title')}</Typography>
              <Typography component="p">{translate('footer_seo_games_description')}</Typography>

              <Typography component="h3">{translate('footer_seo_slots_title')}</Typography>
              <Typography component="p">{translate('footer_seo_slots_description')}</Typography>

              <Typography component="h3">{translate('footer_seo_live_casino_title')}</Typography>
              <Typography component="p">{translate('footer_seo_live_casino_description')}</Typography>

              <Typography component="h3">{translate('footer_seo_sports_betting_title')}</Typography>
              <Typography component="p">{translate('footer_seo_sports_betting_description')}</Typography>

              <Typography component="h2">{translate('footer_seo_get_started_title')}</Typography>
              <Typography component="p">{translate('footer_seo_get_started_description')}</Typography>
              <Box component="ol">
                <li>{translate('footer_seo_step_1')}</li>
                <li>{translate('footer_seo_step_2')}</li>
                <li>{translate('footer_seo_step_3')}</li>
                <li>{translate('footer_seo_step_4')}</li>
              </Box>
              <Typography component="p">{translate('footer_seo_note')}</Typography>

              <Typography component="h2">{translate('footer_seo_promotions_title')}</Typography>
              <Typography component="p">{translate('footer_seo_promotions_description')}</Typography>
              <Typography component="h3">{translate('footer_seo_promo_1_title')}</Typography>
              <Typography component="p">{translate('footer_seo_promo_1_description')}</Typography>
              <Typography component="h3">{translate('footer_seo_promo_2_title')}</Typography>
              <Typography component="p">{translate('footer_seo_promo_2_description')}</Typography>
              <Typography component="h3">{translate('footer_seo_promo_3_title')}</Typography>
              <Typography component="p">{translate('footer_seo_promo_3_description')}</Typography>
              <Typography component="h3">{translate('footer_seo_promo_4_title')}</Typography>
              <Typography component="p">{translate('footer_seo_promo_4_description')}</Typography>
              <Typography component="p">{translate('footer_seo_promo_4_note')}</Typography>

              <Typography component="h2">{translate('footer_seo_faq_title')}</Typography>
              <Typography component="h3">{translate('footer_seo_faq_1_question')}</Typography>
              <Typography component="p">{translate('footer_seo_faq_1_answer')}</Typography>
              <Typography component="h3">{translate('footer_seo_faq_2_question')}</Typography>
              <Typography component="p">{translate('footer_seo_faq_2_answer')}</Typography>
              <Typography component="h3">{translate('footer_seo_faq_3_question')}</Typography>
              <Typography component="p">{translate('footer_seo_faq_3_answer')}</Typography>
            </SeoContent>
          </Collapse>

          <Button
            type="button"
            variant="text"
            onClick={() => setShowSeoContent((prev) => !prev)}
            sx={{ mt: 1.5, px: 0, minWidth: 'auto', textTransform: 'none', textDecoration: 'underline' }}
            aria-label={showSeoContent ? translate('show_less') : translate('show_more')}
          >
            {showSeoContent ? translate('show_less') : translate('show_more')}
          </Button>
        </Box>
      </Box>

      {/* Copyright */}
      <Typography align="center" sx={{ mt: 4, fontSize: '11px', color: '#888' }}>
        {copyrightText}
      </Typography>
    </Box>
  );
};

export default Footer;
